/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';
import { StandaloneOnlineMergeFlow } from './StandaloneOnlineMergeFlow';
import { useValidateRewardsAccountMutation } from '../api/postValidateRewards';
import type { ValidateRewardsModalProps } from './ValidateRewardsModal/ValidateRewardsModal';
import { useAccountTypeModal } from '../hooks/useAccountTypeModal';

const LazyPhoneValidationModal = dynamic(
  () =>
    import('./PhoneValidation/PhoneValidationModal').then(
      ({ PhoneValidationModal }) => PhoneValidationModal,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyRewardsMergeConfirmationModal = dynamic(
  () =>
    import('./RewardsMergeConfirmationModal/RewardsMergeConfirmationModal').then(
      ({ RewardsMergeConfirmationModal }) => RewardsMergeConfirmationModal,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyValidateRewardsModal = dynamic<ValidateRewardsModalProps>(
  () =>
    import('./ValidateRewardsModal/ValidateRewardsModal').then(
      ({ ValidateRewardsModal }) => ValidateRewardsModal,
    ),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyCompletedAutoCreatedAccountModal = dynamic(
  () =>
    import('./CompletedAutoCreatedAccountModal/CompletedAutoCreatedAccountModal').then(
      ({ CompletedAutoCreatedAccountModal }) => CompletedAutoCreatedAccountModal,
    ),
  { loading: () => <ProgressModal noScroll /> },
);

export function RewardsModals() {
  const { loginConfirmationState } = useLoginConfirmation();
  const { modalType } = loginConfirmationState;
  useAccountTypeModal();

  const validateRewardsAccountMutation = useValidateRewardsAccountMutation({});

  switch (modalType?.flow) {
    case 'SHOW_REWARD_SIGNIN':
      return <StandaloneOnlineMergeFlow view={modalType.view} />;

    case 'VALIDATE_REWARD_QUESTIONS':
      return (
        <LazyValidateRewardsModal validateRewardsAccountMutation={validateRewardsAccountMutation} />
      );

    case 'SHOW_REWARD_CONFIRMATION':
    case 'SHOW_CREATE_ACCOUNT_CONFIRMATION':
      return (
        <LazyRewardsMergeConfirmationModal
          validateRewardsAccountData={validateRewardsAccountMutation.data}
        />
      );

    case 'PHONE_OTP_VALIDATION':
      return <LazyPhoneValidationModal view={modalType.view} />;

    case 'COMPLETE_AUTO_CREATED_ACCOUNT':
      return <LazyCompletedAutoCreatedAccountModal view={modalType.view} />;

    default:
      return null;
  }
}
